<template>
  <div class="h-dvh flex flex-row">
    <DSSidebar
      v-if="!isEmptyApp"
      :collapsed="!isNavbarOpened"
    >
      <template #logo>
        <div class="px-2">
          <AppLogo
            :full="isNavbarOpened"
            class="h-6"
          /> 
        </div>
      </template>
      <template
        v-if="$slots.sidebarTop"
        #top
      >
        <slot name="sidebarTop" />
      </template>
      <slot
        name="sidebar"
        :is-navbar-opened
      />
      <template #footer>
        <DSSidebarItem
          label="Need Help?"
          :icon="MessageCircleQuestionIcon"
          @click="handleNeedHelp"
        />
        <slot name="sidebarFooter" />
        <DSSidebarCollapseToggle @toggle="handleToggleCollapse" />
      </template>
    </DSSidebar>

    <div class="flex flex-col flex-auto min-w-0">
      <AppDisruptionMessage
        v-if="isDisruptionMessageVisible"
      />
      <DSHeader>
        <template #repository="{ isShrunk }">
          <slot
            name="repository"
            :is-shrunk
          />
        </template>
        <template #search="{ isShrunk }">
          <DSHeaderSearchTrigger
            v-if="!isEmptyApp && !disableSearch"
            placeholder="Search entities..."
            shortcut="⌘K"
            :is-shrunk
            :class="isShrunk ? 'ml-auto' : undefined"
            data-refid="headerSearchTrigger"
            @click="handleOpenSearch"
          />
          <div
            v-else
            class="flex-auto"
          />
        </template>
        <template #actions="{ isShrunk }">
          <PowerModePopover
            v-if="isPowerMode"
          >
            <DSHeaderPowerModeBadge :is-shrunk />
          </PowerModePopover>
          <NotificationsPopover>
            <DSHeaderNotificationsButton has-unread />
          </NotificationsPopover>
          <div class="w-px h-6 bg-border" />
          <UserDropdown />
        </template>
      </DSHeader>

      <div class="flex flex-auto flex-row min-w-0 md:overflow-hidden">
        <slot name="panel" />
        <main
          data-refid="mainDesktop"
          class="w-full md:w-auto flex flex-col flex-auto px-default pb-0 md:overflow-auto"
        >
          <AppAlerts />
          <AppWelcomeScreen
            v-if="isWelcomeScreenVisible"
            :is-empty-app
            :is-empty-analytics
            :is-empty-connectors
          />
          <template v-else>
            <slot name="content" />
          </template>
        </main>
      </div>
    </div>
    <AppModalTray />
  </div>
</template>

<script setup lang="ts">
import { ComputedRef, inject, markRaw } from 'vue'
import { useLocalStorage } from '@vueuse/core'

import { IS_DISRUPTION_MESSAGE_VISIBLE, POWER_MODE } from '@/const'

import { useModalsStore } from '@/store/modals'

import { useHotKeys } from '@/plugins/hotKeys'
import { useHubspot } from '@/plugins/hubspot'

import { useMainWrapper } from '../utils/useMainWrapper'

import HubspotUnavailableDialog from '../components/HubspotUnavailableDialog.vue'
import NotificationsPopover from './NotificationsPopover.vue'
import PowerModePopover from './PawerModePopover.vue'
import SearchDialog from './SearchDialog.vue'
import UserDropdown from './UserDropdown.vue'

import { AppAlerts, AppDisruptionMessage, AppLogo, AppModalTray, AppWelcomeScreen } from '@app'
import { 
  DSHeader, 
  DSHeaderNotificationsButton, 
  DSHeaderPowerModeBadge,
  DSHeaderSearchTrigger,
  DSSidebar, 
  DSSidebarCollapseToggle, 
  DSSidebarItem 
} from '@ds'

import { MessageCircleQuestion as MessageCircleQuestionIcon } from '@lucide/vue'

defineProps<{
  disableSearch?: boolean
}>()

const modalsStore = useModalsStore()
const hubspot = useHubspot()

const { isEmptyApp, isEmptyAnalytics, isEmptyConnectors, isWelcomeScreenVisible } = useMainWrapper()

const isNavbarOpened = useLocalStorage<boolean>('NAVBAR_OPENED', false)

const isDisruptionMessageVisible = inject<boolean>(IS_DISRUPTION_MESSAGE_VISIBLE, false)
const isPowerMode = inject<ComputedRef<boolean>>(POWER_MODE)
    
const handleToggleCollapse = () => {
  isNavbarOpened.value = !isNavbarOpened.value
}

const handleNeedHelp = () => {
  if (hubspot?.isLoaded) {
    hubspot.open()
  } else {
    const modalInstance = modalsStore.init('hubspot-unavailable', markRaw(HubspotUnavailableDialog))
    modalInstance?.open(modalsStore.getZIndex())
  }
}

const handleOpenSearch = () => {
  const modalInstance = modalsStore.init('global-search', markRaw(SearchDialog))
  modalInstance?.open(modalsStore.getZIndex())
}

useHotKeys('search', handleOpenSearch)

useHotKeys('navbar', () => {
  isNavbarOpened.value = !isNavbarOpened.value
})
</script>
